import React, { useEffect, useState } from 'react';
import { Radio, Loader2, Plus, Pencil, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import {
  mentorFetchSessions,
  mentorCreateSession,
  mentorUpdateSession,
  toInputValue,
  toIso,
  fmtRange,
  SessionPayload,
} from '../../features/liveTrade/api/liveTrade';
import type { LiveSession } from '../../types';
import { StatusBadge } from '../../components/common/StatusBadge';
import { LIVE } from '../../constants/testIds';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { DateTimeField } from '../../components/common/DatePickerFields';

const inputClass =
  'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';

interface FormState {
  title: string;
  description: string;
  session_type: 'public' | 'member' | 'vip';
  meeting_url: string;
  starts_at: string;
  ends_at: string;
  status: 'scheduled' | 'ongoing' | 'completed' | 'cancelled';
  is_published: boolean;
}
const empty: FormState = {
  title: '', description: '', session_type: 'member', meeting_url: '',
  starts_at: '', ends_at: '', status: 'scheduled', is_published: true,
};

export default function MentorLiveTradesPage() {
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState<LiveSession | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    mentorFetchSessions().then(setSessions).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setDialog(true); };
  const openEdit = (s: LiveSession) => {
    setEditing(s);
    setForm({
      title: s.title, description: s.description, session_type: s.session_type,
      meeting_url: s.meeting_url || '', starts_at: toInputValue(s.starts_at),
      ends_at: toInputValue(s.ends_at), status: s.status, is_published: s.is_published,
    });
    setDialog(true);
  };

  const save = async () => {
    setSaving(true);
    const payload: SessionPayload = {
      title: form.title, description: form.description, session_type: form.session_type,
      meeting_url: form.meeting_url, starts_at: toIso(form.starts_at), ends_at: toIso(form.ends_at),
      status: form.status, is_published: form.is_published,
    };
    try {
      if (editing) await mentorUpdateSession(editing.id, payload);
      else await mentorCreateSession(payload);
      toast.success('Sesi tersimpan');
      setDialog(false); load();
    } catch (e: any) {
      toast.error('Gagal menyimpan', { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="Kelola Live Trade">
      <div className="max-w-5xl" data-testid={LIVE.mentorPage}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
              <Radio className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Mentor</p>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Sesi Saya</h2>
            </div>
          </div>
          <button data-testid={LIVE.mentorNewBtn} onClick={openNew} className="bg-cyan-400 text-[#050914] font-medium px-5 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 flex items-center gap-2">
            <Plus className="w-4 h-4" strokeWidth={2} /> Sesi Baru
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : sessions.length === 0 ? (
          <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Anda belum membuat sesi.</p>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.id} data-testid={LIVE.mentorRow} className="flex items-center gap-4 bg-white/[0.02] border border-white/10 rounded-xl p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium truncate">{s.title}</p>
                    <span className="text-[10px] uppercase tracking-wider text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 px-2 py-0.5 rounded-full">{s.session_type}</span>
                    {!s.is_published && <span className="text-[10px] uppercase tracking-wider text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2 py-0.5 rounded-full">Draft</span>}
                  </div>
                  <p className="text-xs text-white/40 flex items-center gap-1.5 mt-1"><Calendar className="w-3.5 h-3.5" strokeWidth={1.5} /> {fmtRange(s.starts_at, s.ends_at)}</p>
                </div>
                <StatusBadge status={s.status} />
                <button onClick={() => openEdit(s)} className="p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5 transition-colors"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Sesi' : 'Sesi Baru'}</DialogTitle>
            <DialogDescription className="text-white/50">Sesi yang Anda buat otomatis menjadi milik Anda.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className={labelClass}>Judul</label>
              <input className={inputClass} data-testid="mentor-live-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Deskripsi</label>
              <textarea className={inputClass} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Tipe Akses</label>
                <select className={inputClass} value={form.session_type} onChange={(e) => setForm({ ...form, session_type: e.target.value as any })}>
                  <option value="public" className="bg-[#090D1A]">Public</option>
                  <option value="member" className="bg-[#090D1A]">Member</option>
                  <option value="vip" className="bg-[#090D1A]">VIP</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Status</label>
                <select className={inputClass} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as any })}>
                  <option value="scheduled" className="bg-[#090D1A]">Scheduled</option>
                  <option value="ongoing" className="bg-[#090D1A]">Ongoing</option>
                  <option value="completed" className="bg-[#090D1A]">Completed</option>
                  <option value="cancelled" className="bg-[#090D1A]">Cancelled</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Meeting URL</label>
              <input className={inputClass} value={form.meeting_url} onChange={(e) => setForm({ ...form, meeting_url: e.target.value })} placeholder="https://meet.google.com/..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Mulai</label>
                <DateTimeField withTime testId="mentor-live-form-start" value={form.starts_at} onChange={(v) => setForm({ ...form, starts_at: v })} />
              </div>
              <div>
                <label className={labelClass}>Selesai</label>
                <DateTimeField withTime testId="mentor-live-form-end" value={form.ends_at} onChange={(v) => setForm({ ...form, ends_at: v })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
              <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="accent-cyan-400 w-4 h-4" /> Publish sesi
            </label>
          </div>
          <DialogFooter>
            <button data-testid={LIVE.mentorSaveBtn} onClick={save} disabled={saving || !form.title} className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />} Simpan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
